// A4 Reviu Persyaratan (stage pelaksanaan_pemilihan): the requirement
// matrix of a paket project.
//   source   the document identity, then the stored Dokumen Pemilihan (the
//            newest file of that type, read by docType; the default once
//            there is one), pasted text or a project file (PDF, DOCX,
//            XLSX, TXT); Unggah Dokumen Pemilihan stores one right here;
//            Analisis dengan Pluto replaces the matrix (asked first when it
//            has rows)
//   counts   butir, sesuai, belum sesuai, belum diperiksa (live from the draft)
//   tabs     Semua, Administrasi, Kualifikasi, Teknis, Harga (and Perlu
//            klasifikasi while any row has no category), each with its count;
//            a tab shows only its rows and a new row starts in it
//   matrix   one row per requirement: the text, the category, the status
//            (Belum diperiksa, Sesuai, Belum sesuai, Tidak berlaku) and the
//            evidence; rows are added and deleted locally, Simpan matriks
//            writes them
import { useEffect, useId, useRef, useState, type ChangeEvent } from "react";
import * as API from "../../api";
import type { ProjectFiles } from "../../api";
import { checkProjectFiles } from "../files";
import { Field, Notice, SkeletonRows, Tabs, useAsync } from "../../ui/components";
import { Glyph } from "../../ui/icons";
import * as Work from "./api";
import type { RequirementCategory, RequirementMatrix, RequirementStatus } from "./api";
import { CATEGORIES, CATEGORY_LABEL, CountTiles, STATUSES, STATUS_LABEL, WorkSection, localKey } from "./shared";

interface RowDraft {
  key: string;
  id?: string;
  text: string;
  category: RequirementCategory;
  status: RequirementStatus;
  evidence: string;
}

const PASTE = "tempel";
/** The newest stored dokumen pemilihan, read by its document type. */
export const STORED_PEMILIHAN = "jenis:dokumen_pemilihan";
const ALL = "semua";
/** The four parts of the matrix (and of the offer); "lainnya" shows only while a row has it. */
const MAIN_CATEGORIES: RequirementCategory[] = ["administrasi", "kualifikasi", "teknis", "harga"];
/** Files whose text the API can read. */
const READABLE = /\.(pdf|docx|xlsx|txt|md|csv)$/i;

function storedPemilihan(files: ProjectFiles | null): API.ProjectFile[] {
  const group = files?.groups?.find((g) => g.docType === "dokumen_pemilihan");
  return group ? group.files : (files?.files ?? []).filter((f) => f.docType === "dokumen_pemilihan");
}

function draftOf(matrix: RequirementMatrix): RowDraft[] {
  return matrix.items.map((i) => ({ key: i.id, id: i.id, text: i.text, category: i.category, status: i.status, evidence: i.evidence }));
}

export function ReviuPersyaratan({ projectId }: { projectId: string }) {
  const uid = useId();
  const matrix = useAsync<RequirementMatrix>(() => Work.getRequirements(projectId), [projectId]);
  const files = useAsync<ProjectFiles>(() => API.listProjectFiles(projectId), [projectId]);
  const [rows, setRows] = useState<RowDraft[]>([]);
  const [sourceName, setSourceName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [source, setSourceState] = useState(PASTE);
  // Once the user picks a source, a reload of the files does not move it.
  const [sourcePicked, setSourcePicked] = useState(false);
  const setSource = (next: string) => {
    setSourcePicked(true);
    setSourceState(next);
  };
  const [text, setText] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<"extract" | "save" | "upload" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState<string>(ALL);
  const picker = useRef<HTMLInputElement>(null);
  const stored = storedPemilihan(files.data);
  const driveOff = files.data?.driveConnected === false;

  // The stored dokumen pemilihan is the natural source once there is one.
  useEffect(() => {
    if (!sourcePicked && stored.length > 0) setSourceState(STORED_PEMILIHAN);
  }, [sourcePicked, stored.length]);

  const upload = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.currentTarget.files ?? []);
    e.currentTarget.value = "";
    if (picked.length === 0) return;
    const problem = checkProjectFiles(picked);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy("upload");
    setError(null);
    try {
      await API.uploadProjectFiles(projectId, "dokumen_pemilihan", picked);
      files.reload();
      setSource(STORED_PEMILIHAN);
    } catch (err) {
      setError(err instanceof API.ApiError && err.status === 503 ? "Penyimpanan file belum terhubung, jadi dokumen pemilihan belum bisa diunggah. Tempel teks persyaratannya di bawah." : API.errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  // A fresh load (or an extraction) resets the draft.
  useEffect(() => {
    if (!matrix.data) return;
    setRows(draftOf(matrix.data));
    setSourceName(matrix.data.sourceName);
    setDirty(false);
  }, [matrix.data]);

  const edit = (key: string, patch: Partial<RowDraft>) => {
    setRows((list) => list.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setDirty(true);
    setSaved(false);
  };

  const readable = (files.data?.files ?? []).filter((f) => READABLE.test(f.name));
  const canAnalyze = source === PASTE ? text.trim().length > 0 : source === STORED_PEMILIHAN ? stored.length > 0 : readable.some((f) => f.driveFileId === source);

  const analyze = async () => {
    setConfirming(false);
    setBusy("extract");
    setError(null);
    try {
      const input: Work.ExtractInput = source === PASTE ? { text } : source === STORED_PEMILIHAN ? { docType: "dokumen_pemilihan" } : { fileId: source };
      if (sourceName.trim()) input.sourceName = sourceName.trim();
      matrix.set(await Work.extractRequirements(projectId, input));
      setSaved(false);
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy("save");
    setError(null);
    try {
      const next = await Work.saveRequirements(projectId, {
        sourceName: sourceName.trim(),
        items: rows.map((r) => ({ id: r.id, text: r.text.trim(), category: r.category, status: r.status, evidence: r.evidence.trim() })),
      });
      matrix.set(next);
      setSaved(true);
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const count = (s: RequirementStatus) => rows.filter((r) => r.status === s).length;
  const inCategory = (c: RequirementCategory) => rows.filter((r) => r.category === c).length;
  const unclassified = inCategory("lainnya");
  const tabs = [
    { id: ALL, label: `Semua (${rows.length})` },
    ...MAIN_CATEGORIES.map((c) => ({ id: c, label: `${CATEGORY_LABEL[c]} (${inCategory(c)})` })),
    ...(unclassified > 0 || tab === "lainnya" ? [{ id: "lainnya", label: `${CATEGORY_LABEL.lainnya} (${unclassified})` }] : []),
  ];
  const shownRows = tab === ALL ? rows : rows.filter((r) => r.category === tab);
  const emptyRow = rows.some((r) => !r.text.trim());
  const method = matrix.data?.method;

  let body;
  if (matrix.loading && !matrix.data) body = <SkeletonRows count={3} label="Memuat matriks persyaratan" />;
  else if (matrix.error && !matrix.data) {
    body = (
      <Notice
        tone="danger"
        title="Matriks belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={matrix.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(matrix.error)}</p>
      </Notice>
    );
  } else {
    body = (
      <>
        <div className="wk-card">
          <h3 className="wk-subtitle">Sumber persyaratan</h3>
          <div className="wk-fields">
            <Field id={`${uid}-name`} label="Identitas dokumen dan versi/adendum" wide>
              <input id={`${uid}-name`} value={sourceName} maxLength={300} placeholder="Contoh: Dokumen Pemilihan Versi 2, Adendum 1" onChange={(e) => (setSourceName(e.currentTarget.value), setDirty(true))} />
            </Field>
            <Field
              id={`${uid}-source`}
              label="Ambil dari"
              hint={
                files.data && stored.length === 0
                  ? "Belum ada Dokumen Pemilihan tersimpan. Unggah di sini, atau tempel teks persyaratannya."
                  : source === STORED_PEMILIHAN && stored[0]
                    ? `Pluto membaca ${stored[0].name}, Dokumen Pemilihan terbaru di project ini.`
                    : undefined
              }
              wide
            >
              <div className="wk-source">
                <select id={`${uid}-source`} value={source} aria-describedby={`${uid}-source-hint`} onChange={(e) => setSource(e.currentTarget.value)}>
                  <option value={STORED_PEMILIHAN} disabled={stored.length === 0}>
                    {stored.length > 0 ? "Dokumen Pemilihan tersimpan (terbaru)" : "Dokumen Pemilihan tersimpan (belum ada)"}
                  </option>
                  <option value={PASTE}>Teks yang ditempel</option>
                  {readable.map((f) => (
                    <option key={f.driveFileId} value={f.driveFileId}>
                      Berkas: {f.name}
                    </option>
                  ))}
                </select>
                <input ref={picker} type="file" multiple accept=".pdf,.docx,.xlsx,.txt" hidden aria-label="Pilih file Dokumen Pemilihan" onChange={(e) => void upload(e)} />
                <button type="button" className="btn btn-secondary" disabled={busy !== null || driveOff} aria-busy={busy === "upload" || undefined} onClick={() => picker.current?.click()}>
                  <Glyph name="add" size={18} />
                  <span>{busy === "upload" ? "Mengunggah" : "Unggah Dokumen Pemilihan"}</span>
                </button>
              </div>
            </Field>
            {source === PASTE ? (
              <Field id={`${uid}-text`} label="Ketentuan dari dokumen pemilihan" hint="Salin bagian persyaratan administrasi, kualifikasi, teknis, dan harga. Satu persyaratan per baris paling mudah dibaca." wide>
                <textarea
                  id={`${uid}-text`}
                  rows={6}
                  maxLength={60_000}
                  value={text}
                  placeholder={"Memiliki NIB yang masih berlaku sesuai bidang usaha\nMelampirkan daftar pengalaman sesuai LDK\nMelampirkan metode kerja dan jadwal pelaksanaan"}
                  onChange={(e) => setText(e.currentTarget.value)}
                />
              </Field>
            ) : null}
          </div>
          {confirming ? (
            <Notice
              tone="warning"
              title={`Matriks yang ada (${rows.length} butir) akan diganti`}
              action={
                <span className="wk-actions">
                  <button type="button" className="btn btn-primary" onClick={() => void analyze()}>
                    Ganti matriks
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setConfirming(false)}>
                    Batal
                  </button>
                </span>
              }
            >
              <p>Status dan bukti yang sudah diisi ikut terhapus.</p>
            </Notice>
          ) : (
            <div className="wk-actions">
              <button type="button" className="btn btn-primary" disabled={!canAnalyze || busy !== null} onClick={() => (rows.length > 0 ? setConfirming(true) : void analyze())}>
                <Glyph name="magicStar" size={18} />
                <span>{busy === "extract" ? "Pluto sedang membaca..." : "Analisis dengan Pluto"}</span>
              </button>
            </div>
          )}
        </div>

        <CountTiles
          label="Ringkasan matriks"
          tiles={[
            { label: "Butir persyaratan", value: rows.length },
            { label: "Sesuai", value: count("sesuai") },
            { label: "Belum sesuai", value: count("belum_sesuai"), note: "Gap yang perlu diselesaikan" },
            { label: "Belum diperiksa", value: count("belum_diperiksa") },
          ]}
        />

        {method && rows.length > 0 ? (
          <p className="wk-note">
            {method === "pluto" ? "Klasifikasi awal oleh Pluto." : method === "aturan" ? "Klasifikasi awal berdasarkan kata kunci (Pluto sedang tidak dapat dihubungi)." : "Matriks disusun manual."} Periksa setiap butir terhadap dokumen asli, dan tandai Sesuai hanya bila buktinya nyata.
          </p>
        ) : null}

        {rows.length > 0 ? <Tabs label="Kategori persyaratan" items={tabs} value={tab} onChange={setTab} idBase={`${uid}-cat`} /> : null}

        {rows.length === 0 ? (
          <p className="wk-empty">Belum ada persyaratan. Tempel ketentuan atau pilih berkas, lalu Analisis dengan Pluto, atau tambahkan satu per satu.</p>
        ) : shownRows.length === 0 ? (
          <p className="wk-empty" id={`${uid}-cat-panel`} role="tabpanel">
            Belum ada persyaratan {CATEGORY_LABEL[tab as RequirementCategory]?.toLowerCase() ?? ""}. Tambahkan bila dokumen pemilihan memintanya.
          </p>
        ) : (
          <ol className="wk-list" aria-label={tab === ALL ? "Matriks persyaratan" : `Matriks persyaratan ${CATEGORY_LABEL[tab as RequirementCategory]}`} id={`${uid}-cat-panel`} role="tabpanel">
            {shownRows.map((r) => ({ r, index: rows.indexOf(r) })).map(({ r, index }) => (
              <li key={r.key} className="wk-row" data-status={r.status}>
                <div className="wk-row-head">
                  <span className="wk-code num">{r.id ?? `Baru ${index + 1}`}</span>
                  <button
                    type="button"
                    className="ui-icon-btn"
                    aria-label={`Hapus persyaratan ${r.id ?? index + 1}`}
                    title="Hapus"
                    onClick={() => (setRows((list) => list.filter((x) => x.key !== r.key)), setDirty(true), setSaved(false))}
                  >
                    <Glyph name="trash" />
                  </button>
                </div>
                <label className="wk-sr" htmlFor={`${uid}-${r.key}-text`}>
                  Bunyi persyaratan
                </label>
                <textarea
                  id={`${uid}-${r.key}-text`}
                  className="wk-row-text"
                  rows={2}
                  maxLength={1_000}
                  value={r.text}
                  aria-invalid={!r.text.trim() || undefined}
                  onChange={(e) => edit(r.key, { text: e.currentTarget.value })}
                />
                <div className="wk-row-fields">
                  <label className="wk-mini">
                    <span>Kategori</span>
                    <select value={r.category} onChange={(e) => edit(r.key, { category: e.currentTarget.value as RequirementCategory })}>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {CATEGORY_LABEL[c]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="wk-mini">
                    <span>Status</span>
                    <select value={r.status} onChange={(e) => edit(r.key, { status: e.currentTarget.value as RequirementStatus })}>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="wk-mini wk-mini-wide">
                    <span>Bukti atau catatan</span>
                    <input value={r.evidence} maxLength={1_000} placeholder="Contoh: NIB No. ..., lampiran 02" onChange={(e) => edit(r.key, { evidence: e.currentTarget.value })} />
                  </label>
                </div>
              </li>
            ))}
          </ol>
        )}

        <div className="wk-actions">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={rows.length >= 150}
            onClick={() => {
              // A new row starts in the open category tab.
              const category: RequirementCategory = tab === ALL ? "lainnya" : (tab as RequirementCategory);
              setRows((list) => [...list, { key: localKey(), text: "", category, status: "belum_diperiksa", evidence: "" }]);
              setDirty(true);
              setSaved(false);
            }}
          >
            <Glyph name="add" size={18} />
            <span>Tambah persyaratan</span>
          </button>
          <button type="button" className="btn btn-primary" disabled={!dirty || emptyRow || busy !== null} onClick={() => void save()}>
            {busy === "save" ? "Menyimpan..." : "Simpan matriks"}
          </button>
          {emptyRow ? <span className="wk-hint">Isi atau hapus baris yang masih kosong.</span> : saved && !dirty ? <span className="wk-hint">Tersimpan.</span> : null}
        </div>
      </>
    );
  }

  return (
    <WorkSection id="reviu-persyaratan" title="Reviu Persyaratan" lead="Petakan setiap syarat dokumen pemilihan terhadap bukti perusahaan. Status di sini adalah penilaian internal, bukan putusan Pokja.">
      {body}
      {error ? (
        <Notice tone="danger" title="Belum berhasil">
          <p>{error}</p>
        </Notice>
      ) : null}
    </WorkSection>
  );
}
